import { buildMediaSlots, computeAspectRatio } from '../../media'
import { groupMediaBySubGroup } from '../../buildMediaListItems'

export function getGeneratedImageId(item) {
  if (!item) {
    return null
  }

  const id = item.id ?? item.image_url

  return id != null ? String(id) : null
}

function normalizeGeneratedImageItem(item) {
  return {
    ...item,
    ...(item.id != null ? { id: String(item.id) } : {}),
    ...(item.group_id != null ? { group_id: String(item.group_id) } : {}),
    ...(item.sub_group_id != null ? { sub_group_id: String(item.sub_group_id) } : {}),
  }
}

export function normalizePostGeneratedImagesResult(data) {
  if (!data) {
    return null
  }

  const result = Array.isArray(data) ? data : data.result ?? data

  if (!Array.isArray(result)) {
    return null
  }

  return result.map(normalizeGeneratedImageItem)
}

const buildTemporarySubGroupId = (fallbackMediaId, item) => {
  const headlineKey = item.headline ?? '__no_headline__'
  return `tmp-${fallbackMediaId}-${headlineKey}`
}

function mapGeneratedImage(item, originalGroupId, subGroupId = null) {
  const meta = item.meta_data ?? {}
  const width = item.width ?? meta.width ?? null
  const height = item.height ?? meta.height ?? null
  // Prefer client-calculated ratio from pixels over BE label (can be wrong).
  const calculatedRatio = width && height ? computeAspectRatio(width, height) : null

  return {
    id: getGeneratedImageId(item),
    url: item.image_url,
    filename: item.image_name,
    width,
    height,
    size: item.size ?? meta.size ?? null,
    ratio: calculatedRatio ?? item.ratio ?? null,
    group_id: item.group_id ?? originalGroupId,
    sub_group_id: item.sub_group_id ?? subGroupId,
    headline: item.headline ?? null,
    is_generated: true,
  }
}

function groupGeneratedItemsByGroupId(items) {
  return items.reduce((acc, item) => {
    const mediaId = String(item.group_id ?? '')

    if (!acc[mediaId]) {
      acc[mediaId] = []
    }

    acc[mediaId].push(item)
    return acc
  }, {})
}

export function mapGeneratedImagesResponse(result) {
  if (!Array.isArray(result) || !result.length) return []

  return Object.entries(groupGeneratedItemsByGroupId(result)).map(([mediaId, items]) => {
    const generatedItems = items
      .filter((i) => !i.original)
      .map((item) => {
        // Trust BE sub_group_id for aspect-ratio variants.
        // Temporary ids only for headline groups when BE omitted sub_group_id.
        if (item.sub_group_id != null) {
          return item
        }

        if (item.headline != null) {
          return {
            ...item,
            sub_group_id: buildTemporarySubGroupId(mediaId, item),
          }
        }

        return item
      })

    const groupedBySubGroup = generatedItems.reduce((acc, item) => {
      const subGroupKey = item.sub_group_id

      if (!acc[subGroupKey]) {
        acc[subGroupKey] = {
          id: subGroupKey,
          sub_group_id: item.sub_group_id,
          group_id: item.group_id ?? mediaId,
          headline: item.headline ?? null,
          items: [],
        }
      }

      const group = acc[subGroupKey]

      group.items.push(item)

      if (item.headline != null) {
        group.headline = item.headline
      }

      return acc
    }, {})

    const groups = Object.values(groupedBySubGroup).map((group) => ({
      id: group.id,
      sub_group_id: group.sub_group_id,
      group_id: group.group_id,
      headline: group.headline,
      images: group.items.map((item) =>
        mapGeneratedImage(item, group.group_id, group.sub_group_id),
      ),
    }))

    return {
      id: mediaId,
      groups,
    }
  })
}

function mapMediaFileToRequestItem(file) {
  const ratio = computeAspectRatio(file.width, file.height) ?? file.ratio ?? null

  return {
    id: file.id,
    type: file.type,
    filename: file.name || file.filename,
    url: file.url,
    ...(ratio != null && { ratio }),
    ...(file.size != null && { size: file.size }),
    ...(file.height != null && { height: file.height }),
    ...(file.width != null && { width: file.width }),
  }
}

export function buildPostGeneratedImagesBody({
  eventId,
  tc,
  tcRunId,
  generatedResult,
  selectedIds = [],
}) {
  const selectedSet = new Set(selectedIds)
  const items = Array.isArray(generatedResult) ? generatedResult : []

  const media = items
    .filter((item) => !item.original)
    .map((item) => ({
      ...item,
      selected: selectedSet.has(getGeneratedImageId(item)),
    }))

  return {
    eid: eventId,
    tc,
    tc_run_id: tcRunId,
    media,
  }
}

export function collectSelectedImageIdsFromResult(result) {
  if (!Array.isArray(result)) {
    return []
  }

  const hasSelectionFlags = result.some((item) => typeof item.selected === 'boolean')

  return result
    .filter((item) => {
      if (item.original) {
        return false
      }

      return hasSelectionFlags ? item.selected === true : true
    })
    .map((item) => getGeneratedImageId(item))
}

export default function buildGenerateImagesRequestBody({
  eventId,
  tc,
  tcRunId,
  media,
  selectedSubGroupId,
  ratioVariants,
  headlines = [],
  requiredSlotRatios,
}) {
  const selectedGroup = selectedSubGroupId
    ? groupMediaBySubGroup(media).find(
      (group) => String(group.groupKey) === String(selectedSubGroupId),
    )
    : null

  // UI order: anchor first (first item in subgroup), then remaining group items.
  const selectedGroupItems = selectedGroup?.items ?? []
  const selectedFiles = selectedGroupItems.filter((file) => file.url)
  const filteredHeadlines = headlines.filter(Boolean)
  const anchorFile = selectedFiles[0] ?? null
  const missingAspectRatios = buildMediaSlots(selectedGroupItems, requiredSlotRatios)
    .filter((slot) => !slot.image)
    .map((slot) => slot.ratio)

  const body = {
    eid: eventId,
    tc,
    tc_run_id: tcRunId,
    media: selectedFiles.map((file, index) => ({
      ...mapMediaFileToRequestItem(file),
      original: index === 0,
    })),
    sub_group_id: selectedSubGroupId ?? anchorFile?.sub_group_id ?? null,
    group_id: anchorFile?.id ?? null,
    missing_aspect_ratios: missingAspectRatios,
  }

  if (ratioVariants) {
    body.aspect_ratios = ratioVariants
  }

  body.headlines = filteredHeadlines

  return body
}
